import { isNil } from 'lodash';
import { useMemo } from 'react';
import { formatNumber } from '@shared/components/blocks/lib/inputNumber/util';
import { NumberFormat } from '@shared/scenes/workflows/config';
import { API } from '@shared/types/api';
import { isNumberComponent } from '../../utils';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowTextComponent } from './WorkflowTextComponent';

type Datum = number | undefined;
type Data = Datum | Datum[];

export interface WorkflowNumberComponentProps extends WorkflowComponentProps {
  format?: NumberFormat;
}

/*
  TODO
  This component is only used as a readOnly lookup component.
  Will need changes to use in forms. See inputNumber.
*/
export function WorkflowNumberComponent({
  initialData,
  component,
  isReadOnly,
  showLabel,
  paddingHorizontal,
  iconPad,
  showIcon,
}: WorkflowNumberComponentProps) {
  const numberFormat = (component as API.NumberComponent).format;
  const displayString = useMemo(
    () => formatData(initialData, numberFormat),
    [initialData, numberFormat],
  );
  const readOnly = isReadOnly || (component as API.NumberComponent).readonly;

  if (!isNumberComponent(component)) {
    return null;
  }

  if (readOnly) {
    return (
      <WorkflowTextComponent
        initialData={displayString}
        component={component}
        isReadOnly
        showLabel={showLabel}
        paddingHorizontal={paddingHorizontal}
        iconPad={iconPad}
        showIcon={showIcon}
      />
    );
  }

  return null;
}

function formatData(data: Data, numberFormat: NumberFormat): string | null {
  if (isNil(data)) {
    return null;
  }

  if (Array.isArray(data)) {
    const displayableData = data.filter((d): d is number => !isNil(d));
    return displayableData.length > 0
      ? displayableData.map(d => formatNumber(d, numberFormat)).join(', ')
      : null;
  }

  return formatNumber(data, numberFormat);
}
